/**
 * The reader-pager lock interstitial, as data: a word-capped plain-text teaser
 * of the article body plus the ONE rendering decision (buildInterstitialViewModel)
 * both apps draw verbatim, so the four card states (meter_available,
 * anonymous_free_available, anonymous_free_consumed, meter_exhausted) cannot
 * drift between web and mobile.
 *
 * Pure and platform-agnostic: period copy arrives pre-resolved via
 * `periodCopy` (meterPeriodCopy), read minutes via estimateReadingMinutes,
 * the topic name via the app's domain-list lookup. Rendering the teaser must
 * NEVER spend an unlock — this module only shapes strings; the only spend
 * path stays the apps' unlock flow.
 */

/**
 * Word-capped plain-text excerpt of a (markdown-ish) article body for the
 * lock-card teaser. Heading LINES are dropped entirely (not just their `#`
 * markers) so the teaser starts at the first prose paragraph; `<image>…</image>`
 * content tokens (mobile body format) and markdown decoration are stripped so
 * the result never contains markup.
 *
 * @param {string|null|undefined} articleBody
 * @param {{ maxWords?: number }} [options]
 * @returns {{ text: string, truncated: boolean }}
 */
export function buildArticleTeaser(articleBody, { maxWords = 50 } = {}) {
  if (typeof articleBody !== 'string' || articleBody.trim() === '') {
    return { text: '', truncated: false };
  }

  const plain = articleBody
    .replace(/<image>[\s\S]*?<\/image>/gi, ' ')
    .split('\n')
    .filter((line) => !/^\s*#/.test(line))
    .join('\n')
    // Markdown decoration → plain text. Images vanish, links keep their text.
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (plain === '') return { text: '', truncated: false };

  const words = plain.split(' ');
  if (words.length <= maxWords) return { text: plain, truncated: false };
  return { text: words.slice(0, maxWords).join(' '), truncated: true };
}

/** Meter dots stop reading as a meter past a handful — larger limits fall back to text. */
const MAX_METER_DOTS = 5;

const UPSELL_LABEL = 'Skip the meter — go unlimited';

/**
 * The lock-card rendering decision. Apps pass raw access/meter state plus
 * app-resolved display inputs and render the returned fields verbatim; every
 * null field means "hide that element" (chips, meter row, teaser block).
 *
 * @param {Object} inputs                                spread deriveArticleAccess output in; extra keys (access) are ignored
 * @param {'meter_available'|'anonymous_free_available'|'anonymous_free_consumed'|'meter_exhausted'} inputs.reason
 * @param {number} inputs.remaining                      unlocks left this period
 * @param {number} inputs.limit                          per-period unlock limit
 * @param {{ thisPeriod: string, resetsAdverb: string, everyPeriod: string }} inputs.periodCopy
 *        from meterPeriodCopy(period) — the single period-copy source
 * @param {string} inputs.articleTitle
 * @param {string|null} [inputs.topicName]               null hides the topic chip
 * @param {number|null} [inputs.readMinutes]             estimateReadingMinutes(word_count); null hides the chip
 * @param {{ text: string, truncated: boolean }|null} [inputs.teaser]
 *        buildArticleTeaser(...); empty text hides the teaser block
 * @param {number} inputs.position                       1-based pager index
 * @param {number} inputs.total                          pager list length
 * @param {boolean} [inputs.canUpsell=true]              apps pass !IS_WHITE_LABEL
 * @returns {{
 *   positionLabel: string,
 *   meter: { filled: number, total: number }|null,
 *   meterLabel: string|null,
 *   title: string,
 *   topicName: string|null,
 *   readMinutesLabel: string|null,
 *   teaser: { text: string, truncated: boolean }|null,
 *   ctaLabel: string,
 *   ctaCaption: string,
 *   showUpsellLink: boolean,
 *   upsellLabel: string,
 *   upsellTarget: 'paywall'|'signup',
 * }}
 */
export function buildInterstitialViewModel({
  reason,
  remaining,
  limit,
  periodCopy,
  articleTitle,
  topicName = null,
  readMinutes = null,
  teaser = null,
  position,
  total,
  canUpsell = true,
}) {
  const anonymous = reason === 'anonymous_free_available' || reason === 'anonymous_free_consumed';

  // Meter row: only the signed-in metered states show one (the meter is a
  // post-signup concept). Dots read at a glance up to MAX_METER_DOTS; larger
  // limits get a text-only "N of M left …" line instead.
  let meter = null;
  let meterLabel = null;
  if (!anonymous) {
    if (limit <= MAX_METER_DOTS) {
      meter = { filled: remaining, total: limit };
      meterLabel = `${remaining} left ${periodCopy.thisPeriod}`;
    } else {
      meterLabel = `${remaining} of ${limit} left ${periodCopy.thisPeriod}`;
    }
  }

  let ctaLabel;
  let ctaCaption;
  switch (reason) {
    case 'anonymous_free_available':
      ctaLabel = 'Read free article';
      ctaCaption = `${remaining} free read${remaining === 1 ? '' : 's'} left — no account needed`;
      break;
    case 'anonymous_free_consumed':
      ctaLabel = 'Sign up free';
      ctaCaption = `Get ${limit} article unlocks ${periodCopy.everyPeriod} — free`;
      break;
    case 'meter_exhausted':
      ctaLabel = 'Go unlimited';
      ctaCaption = `Your unlocks reset ${periodCopy.resetsAdverb}`;
      break;
    default: // meter_available
      ctaLabel = 'Unlock article';
      ctaCaption = 'Unlocked articles stay open forever';
      break;
  }

  return {
    positionLabel: `Article ${position} of ${total}`,
    meter,
    meterLabel,
    title: articleTitle,
    topicName: topicName || null,
    readMinutesLabel: readMinutes != null ? `${readMinutes} min read` : null,
    teaser: teaser && teaser.text ? teaser : null,
    ctaLabel,
    ctaCaption,
    // The exhausted card's primary CTA is already the paywall — a link would duplicate it.
    showUpsellLink: Boolean(canUpsell) && reason !== 'meter_exhausted',
    upsellLabel: UPSELL_LABEL,
    upsellTarget: anonymous ? 'signup' : 'paywall',
  };
}
